import React from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DataTable, { TableColumn } from 'react-data-table-component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { isUndefined, isNull } from 'lodash';

import { PolicyBenefitLimit } from '@models/PolicyBenefitLimit';
import { BenefitTemplateTerm } from '@models/BenefitTemplateTerm';
import { InsurancePolicyStatuses } from '@models/InsurancePolicy';
import { formatDate } from '@utils/datetime';
import { formatCurrency } from '@utils/currency';
import { BackButton, Loading, LoadingError } from '@components/Base';
import { InsurancePolicyBadge } from '@components/Feature/InsurancePolicy';

import { useInsurancePolicyUserFetch } from './hook';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const InsurancePolicyUserDetailsPage = () => {
  const { t } = useTranslation();
  const { policyUser, healthPolicyBenefitLimitsWithAccumulatedPaidAmounts } = useInsurancePolicyUserFetch();

  if (isUndefined(policyUser)) return <Loading />;
  if (isNull(policyUser)) return <LoadingError />;

  const {
    user,
    memberId,
    effectiveDate,
    expirationDate,
    ended,
    withinClaimSubmissionDeadline,
    claimSubmissionDeadline,
    insurancePolicy,
    policyBenefitTemplate,
    memberCard,
    memberCardKm,
  } = policyUser;
  const status = ended ? InsurancePolicyStatuses.Ended : InsurancePolicyStatuses.Active;

  const benefitLimitsColumns: TableColumn<PolicyBenefitLimit>[] = [
    {
      name: t('common:benefit'),
      selector: (row) => row.benefit,
      sortable: true,
      wrap: true,
    },
    {
      name: t('common:subBenefit'),
      selector: (row) => row.subBenefit,
      sortable: true,
      wrap: true,
    },
    {
      name: t('common:limitAmount'),
      cell: (row) => formatCurrency(row.limitAmount),
      sortable: true,
    },
    {
      name: t('common:description'),
      selector: (row) => row.description,
      sortable: true,
      wrap: true,
    },
    {
      name: t('common:accumulatedPaidAmount'),
      selector: (row) => formatCurrency(row.accumulatedPaidAmount),
      wrap: true,
    },
  ];

  const benefitTemplateTermsColumns: TableColumn<BenefitTemplateTerm>[] = [
    {
      name: t('user.name'),
      selector: (row) => row.insuranceTerm?.description || '',
      sortable: true,
      wrap: true,
      width: '25%',
    },
    {
      name: t('common:description'),
      cell: (row) => <div dangerouslySetInnerHTML={{ __html: row.descriptionHtml }} />,
      wrap: true,
      width: '75%',
    },
  ];

  return (
    <Container className="my-4">
      <Row className="mb-3">
        <Col>
          <BackButton />
        </Col>
      </Row>

      <Row className="mb-4 align-items-center">
        <Col>
          <h2>{t('policyDetails.title')}</h2>
        </Col>
        <Col xs="auto">
          {memberCard && (
            <a href={memberCard} target="_blank" rel="noopener noreferrer" className="btn btn-primary me-2">
              <FontAwesomeIcon icon={faPrint} className="me-2" />
              {t('policyDetails.printDigitalCard')} (EN)
            </a>
          )}
          {memberCardKm && (
            <a href={memberCardKm} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              <FontAwesomeIcon icon={faPrint} className="me-2" />
              {t('policyDetails.printDigitalCard')} (KM)
            </a>
          )}
        </Col>
      </Row>

      <Card className={`mb-4 ${ended ? 'border-secondary' : 'border-success'}`}>
        <Card.Header className="d-flex flex-row justify-content-between align-items-center">
          <h5 className="mb-0">{t('common:policyInformation')}</h5>
          <InsurancePolicyBadge status={status} />
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('policyDetails.memberName')}:</strong>
              <div>{user.name}</div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:memberId')}:</strong>
              <div>{memberId}</div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:policyNumber')}:</strong>
              <div>{insurancePolicy.policyNumber}</div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:plan')}:</strong>
              <div>{policyBenefitTemplate.name}</div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:effectiveDate')}:</strong>
              <div>{formatDate(effectiveDate)}</div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('common:endDate')}:</strong>
              <div>{formatDate(expirationDate)}</div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('digitalCard.annualLimit')}:</strong>
              <div>{formatCurrency(policyBenefitTemplate.annualLimitAmount)}</div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <strong>{t('digitalCard.geographicCoverage')}:</strong>
              <div>{policyBenefitTemplate.geographicCoverage}</div>
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="mb-3">
              <Alert variant="info">
                <strong>{t('common:remainingLimitAmount')}:</strong>
                <div>{formatCurrency(policyUser.remainingLimitAmount)}</div>
              </Alert>
            </Col>
          </Row>

          {ended && withinClaimSubmissionDeadline && (
            <Alert variant="warning">
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              <span>
                {t('alert.claimSubmission.deadline', { claimSubmissionDeadline: formatDate(claimSubmissionDeadline) })}
              </span>
            </Alert>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">{t('common:coverage')}</h5>
        </Card.Header>
        <Card.Body>
          <DataTable
            columns={benefitLimitsColumns}
            data={healthPolicyBenefitLimitsWithAccumulatedPaidAmounts || []}
            customStyles={customStyles}
            pagination
            fixedHeader
            noDataComponent={<div className="p-3">{t('common:noData')}</div>}
          />
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">{t('common:termsAndConditions')}</h5>
        </Card.Header>
        <Card.Body>
          <DataTable
            columns={benefitTemplateTermsColumns}
            data={policyBenefitTemplate.policyBenefitTemplateTerms || []}
            customStyles={customStyles}
            pagination
            fixedHeader
            noDataComponent={<div className="p-3">{t('common:noData')}</div>}
          />
        </Card.Body>
      </Card>
    </Container>
  );
};

export { InsurancePolicyUserDetailsPage };
